import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { WrappedIntlProvider } from "./wrapped-intl-provider";
import styles from "../assets/stylesheets/link.scss";
import { disableiOSZoom } from "../utils/disable-ios-zoom";
import dashboardChannelSingleton from "../utils/dashboard-channel-singleton";
import { classifyDeviceLinkExchangeError, deviceLinkExchangeRetryDelay } from "../utils/dashboard-channel";
import { Button, Field, Input, Drawer, DrawerBody, ProgressBar } from "@fluentui/react-components";
import { BackspaceRegular } from "@fluentui/react-icons";
import { RuntimeFluentProvider } from "../utils/fluent-theme";
import { CookieConsentModalContainer } from "./CookieConsentModalConainer";
import {
  decryptDeviceLinkPayload,
  exportDeviceLinkPublicKey,
  generateDeviceLinkEncryptionKeyPair
} from "../utils/device-link-crypto";
import {
  DEVICE_LINK_CODE_LENGTH,
  DEVICE_LINK_GROUP_SIZE,
  DEVICE_LINK_KEYPAD_SYMBOLS,
  DEVICE_LINK_SYMBOLS,
  isCompleteDeviceLinkCode,
  normalizeDeviceLinkCode
} from "../utils/device-link-code";

disableiOSZoom();

const ENTERABLE_STATUSES = ["entering", "error"];
const CODE_PROGRESS_STATUSES = ["claiming", "exchanging", "waiting_for_source_confirmation", "success_redirecting"];

function LinkRoot({ store }) {
  const [linkState, setLinkState] = useState({
    entered: "",
    cursorIndex: 0,
    status: "entering",
    error: null
  });
  const linkStateRef = useRef(linkState);
  const codeInputRefs = useRef([]);
  const dashboardChannelRef = useRef(null);

  const commitLinkState = useCallback(nextState => {
    linkStateRef.current = nextState;
    setLinkState(nextState);
    return nextState;
  }, []);

  const updateLinkState = useCallback(
    patch => {
      const currentState = linkStateRef.current;
      const nextPatch = typeof patch === "function" ? patch(currentState) : patch;
      return commitLinkState({ ...currentState, ...nextPatch });
    },
    [commitLinkState]
  );

  const getDashboardChannel = useCallback(() => {
    if (!store) {
      return null;
    }

    if (!dashboardChannelRef.current) {
      dashboardChannelRef.current = dashboardChannelSingleton.getInstance(store, "");
    }

    return dashboardChannelRef.current;
  }, [store]);

  const isEntryDisabled = useCallback(() => {
    return !ENTERABLE_STATUSES.includes(linkStateRef.current.status);
  }, []);

  const getCodeCursorIndex = useCallback(() => {
    const { cursorIndex, entered } = linkStateRef.current;
    return Math.max(0, Math.min(cursorIndex, entered.length, DEVICE_LINK_CODE_LENGTH));
  }, []);

  const focusCodeInput = useCallback(
    (cursorIndex = getCodeCursorIndex()) => {
      if (isEntryDisabled()) return;

      const inputIndex = Math.min(cursorIndex, DEVICE_LINK_CODE_LENGTH - 1);
      const input = codeInputRefs.current[inputIndex] || codeInputRefs.current[linkStateRef.current.entered.length];
      if (!input) return;

      input.focus?.();
    },
    [getCodeCursorIndex, isEntryDisabled]
  );

  const attemptLink = useCallback(
    async code => {
      try {
        const normalizedCode = normalizeDeviceLinkCode(code);
        updateLinkState({
          status: "claiming",
          error: null,
          entered: normalizedCode,
          cursorIndex: normalizedCode.length
        });

        const keyPair = await generateDeviceLinkEncryptionKeyPair();
        const targetEncryptionPublicJwk = await exportDeviceLinkPublicKey(keyPair);
        const dashboardChannel = getDashboardChannel();
        if (!dashboardChannel) {
          throw new Error("failed");
        }

        const claim = await dashboardChannel.claimDeviceLink({
          userCode: normalizedCode,
          targetEncryptionPublicJwk
        });
        let exchange = null;
        const claimExpiry = Number(claim.expires_at) * 1000;
        const deadline = Number.isFinite(claimExpiry) ? claimExpiry : Date.now() + 300000;

        for (let attempt = 0; attempt < 120; attempt++) {
          updateLinkState({ status: attempt === 0 ? "exchanging" : "waiting_for_source_confirmation" });
          try {
            exchange = await dashboardChannel.exchangeDeviceLinkClaim({
              claimId: claim.claim_id,
              claimSecret: claim.claim_secret
            });
          } catch (error) {
            const outcome = classifyDeviceLinkExchangeError(error);
            if (!outcome.retryable) {
              throw new Error("failed");
            }

            exchange = null;
            const remaining = deadline - Date.now();
            if (remaining <= 0) {
              throw new Error("failed");
            }
            await new Promise(resolve =>
              setTimeout(resolve, Math.min(remaining, deviceLinkExchangeRetryDelay(attempt + 1, outcome.retryAfter)))
            );
            continue;
          }

          if (exchange?.status === "confirmed") {
            break;
          }

          const remaining = deadline - Date.now();
          if (remaining <= 0) {
            throw new Error("failed");
          }
          await new Promise(resolve =>
            setTimeout(resolve, Math.min(remaining, deviceLinkExchangeRetryDelay(attempt + 1)))
          );
        }

        if (exchange?.status !== "confirmed" || !exchange.encrypted_handoff_payload) {
          throw new Error("failed");
        }

        const payload = await decryptDeviceLinkPayload(keyPair.privateKey, exchange.encrypted_handoff_payload);

        if (payload.profile) {
          const { hasChangedName } = store.state.activity;

          if (!hasChangedName) {
            store.update({ activity: { hasChangedName: true }, profile: payload.profile });
          }
        }

        if (exchange.user) {
          store.update({ authUser: exchange.user });
          store.update({
            profile: { ...store.state.profile, user_color: exchange.user.user_color }
          });
        }

        if (payload.path) {
          updateLinkState({ status: "success_redirecting" });
          window.location.href = payload.path;
        }
      } catch (e) {
        updateLinkState({ status: "error", error: "invalid_or_expired", entered: "", cursorIndex: 0 });

        if (!(e instanceof Error && (e.message === "in_use" || e.message === "failed"))) {
          throw e;
        }
      }
    },
    [getDashboardChannel, store, updateLinkState]
  );

  const updateCodeSlot = useCallback(
    (index, value) => {
      if (isEntryDisabled()) return;

      const normalized = normalizeDeviceLinkCode(value);
      const nextChar = normalized ? normalized[normalized.length - 1] : "";
      const slots = Array.from({ length: DEVICE_LINK_CODE_LENGTH }, (_entry, slotIndex) => {
        return linkStateRef.current.entered[slotIndex] || "";
      });
      slots[index] = nextChar;

      const entered = normalizeDeviceLinkCode(slots.join(""));
      const cursorIndex = nextChar ? Math.min(index + 1, DEVICE_LINK_CODE_LENGTH) : index;
      const nextState = updateLinkState({ entered, cursorIndex, status: "entering", error: null });

      focusCodeInput(cursorIndex);

      if (isCompleteDeviceLinkCode(nextState.entered)) {
        attemptLink(nextState.entered);
      }
    },
    [attemptLink, focusCodeInput, isEntryDisabled, updateLinkState]
  );

  const addToEntry = useCallback(
    ch => {
      if (isEntryDisabled()) return;
      const cursorIndex = getCodeCursorIndex();
      updateCodeSlot(cursorIndex, ch);
    },
    [getCodeCursorIndex, isEntryDisabled, updateCodeSlot]
  );

  const removeChar = useCallback(() => {
    if (isEntryDisabled()) return;

    const currentState = linkStateRef.current;
    const cursorIndex = getCodeCursorIndex();
    if (currentState.entered.length === 0 || cursorIndex === 0) return;

    const entered = `${currentState.entered.slice(0, cursorIndex - 1)}${currentState.entered.slice(cursorIndex)}`;
    updateLinkState({ entered, cursorIndex: cursorIndex - 1, status: "entering", error: null });
    focusCodeInput(cursorIndex - 1);
  }, [focusCodeInput, getCodeCursorIndex, isEntryDisabled, updateLinkState]);

  const setCodeCursorIndex = useCallback(
    index => {
      if (isEntryDisabled()) return;

      const cursorIndex = Math.max(0, Math.min(index, DEVICE_LINK_CODE_LENGTH));
      updateLinkState({ cursorIndex });
      focusCodeInput(cursorIndex);
    },
    [focusCodeInput, isEntryDisabled, updateLinkState]
  );

  const handleCodeSlotKeyDown = useCallback(
    index => event => {
      if (isEntryDisabled()) return;

      if (event.key === "Backspace") {
        event.preventDefault();
        event.stopPropagation();

        if (linkStateRef.current.entered[index]) {
          updateCodeSlot(index, "");
        } else if (index > 0) {
          setCodeCursorIndex(index - 1);
        }
        return;
      }

      if (event.key === "Delete") {
        event.preventDefault();
        event.stopPropagation();
        updateCodeSlot(index, "");
        return;
      }

      const key = event.key.toUpperCase();
      if (DEVICE_LINK_SYMBOLS.includes(key)) {
        event.preventDefault();
        event.stopPropagation();
        updateCodeSlot(index, key);
      }
    },
    [isEntryDisabled, setCodeCursorIndex, updateCodeSlot]
  );

  const handleCodePaste = useCallback(
    event => {
      if (isEntryDisabled()) return;

      const pasted = normalizeDeviceLinkCode(event.clipboardData.getData("text"));
      if (!pasted) return;

      event.preventDefault();
      const cursorIndex = getCodeCursorIndex();
      const slots = Array.from({ length: DEVICE_LINK_CODE_LENGTH }, (_entry, slotIndex) => {
        return linkStateRef.current.entered[slotIndex] || "";
      });

      pasted.split("").forEach((char, offset) => {
        const slotIndex = cursorIndex + offset;
        if (slotIndex < DEVICE_LINK_CODE_LENGTH) {
          slots[slotIndex] = char;
        }
      });

      const entered = normalizeDeviceLinkCode(slots.join(""));
      const nextCursorIndex = Math.min(cursorIndex + pasted.length, DEVICE_LINK_CODE_LENGTH, entered.length);
      const nextState = updateLinkState({
        entered,
        cursorIndex: nextCursorIndex,
        status: "entering",
        error: null
      });

      focusCodeInput(nextCursorIndex);

      if (isCompleteDeviceLinkCode(nextState.entered)) {
        attemptLink(nextState.entered);
      }
    },
    [attemptLink, focusCodeInput, getCodeCursorIndex, isEntryDisabled, updateLinkState]
  );

  const handleKeyDown = useCallback(
    e => {
      if (e.target?.tagName === "INPUT") {
        return;
      }

      if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault();
        e.stopPropagation();
        removeChar();
        return;
      }

      const key = e.key.toUpperCase();
      if (!DEVICE_LINK_SYMBOLS.includes(key)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      addToEntry(key);
    },
    [addToEntry, removeChar]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const isEntryDisabledForRender = isEntryDisabled();
  const showCodeProgress = CODE_PROGRESS_STATUSES.includes(linkState.status);
  const cursorIndex = getCodeCursorIndex();
  const codeSlots = useMemo(
    () =>
      Array.from({ length: DEVICE_LINK_CODE_LENGTH }, (_, index) => ({
        index,
        value: linkState.entered[index] || "",
        isActive: !isEntryDisabledForRender && cursorIndex === index
      })),
    [cursorIndex, isEntryDisabledForRender, linkState.entered]
  );
  const codeSlotGroups = useMemo(() => {
    const groups = [];

    for (let index = 0; index < codeSlots.length; index += DEVICE_LINK_GROUP_SIZE) {
      groups.push(codeSlots.slice(index, index + DEVICE_LINK_GROUP_SIZE));
    }

    return groups;
  }, [codeSlots]);

  const keypadSymbolButtons = DEVICE_LINK_KEYPAD_SYMBOLS.map(symbol => (
    <Button
      appearance="subtle"
      disabled={
        isEntryDisabledForRender ||
        (linkState.entered.length >= DEVICE_LINK_CODE_LENGTH && cursorIndex >= DEVICE_LINK_CODE_LENGTH)
      }
      className={styles.keypadButton}
      key={`char_${symbol}`}
      onClick={() => addToEntry(symbol)}
    >
      {symbol}
    </Button>
  ));
  const statusMessage =
    linkState.status === "error" ? (
      <FormattedMessage id="link-page.try-again" defaultMessage="We couldn't find that code, please try again." />
    ) : linkState.status === "waiting_for_source_confirmation" ? (
      <FormattedMessage id="link-page.waiting" defaultMessage="Waiting for confirmation on the first device" />
    ) : linkState.status === "claiming" || linkState.status === "exchanging" ? (
      <FormattedMessage id="link-page.claiming" defaultMessage="Checking code..." />
    ) : linkState.status === "success_redirecting" ? (
      <FormattedMessage id="link-page.redirecting" defaultMessage="Opening Verse..." />
    ) : null;

  return (
    <RuntimeFluentProvider>
      <WrappedIntlProvider>
        <Drawer separator backdropMotion={null} surfaceMotion={null} size="full" open={true}>
          <DrawerBody className={styles.body}>
            <div className={styles.linkPanel}>
              <div className={styles.enteredContents}>
                <div className={styles.contentHeader}>
                  <div className={styles.headerEyebrow}>
                    <FormattedMessage id="link-page.welcome" defaultMessage="Welcome" />
                  </div>
                  <h1 className={styles.contentTitle}>
                    <FormattedMessage id="link-page.enter-code" defaultMessage="Enter Verse Code" />
                  </h1>
                </div>

                {statusMessage && <div className={styles.subHeader}>{statusMessage}</div>}

                {showCodeProgress && (
                  <Field className={styles.codeProgress} validationState="none">
                    <ProgressBar thickness="large" />
                  </Field>
                )}

                {linkState.status === "waiting_for_source_confirmation" && (
                  <div className={styles.statusHint}>
                    <FormattedMessage
                      id="link-page.keep-open"
                      defaultMessage="Keep this page open until the Verse opens."
                    />
                  </div>
                )}

                <div className={styles.entered}>
                  <FormattedMessage id="link-page.entered-code-label" defaultMessage="Entered Verse code">
                    {codeDisplayLabel => (
                      <>
                        <div
                          className={styles.codeDisplay}
                          aria-label={String(codeDisplayLabel)}
                          onClick={() => focusCodeInput()}
                          onPaste={handleCodePaste}
                        >
                          {codeSlotGroups.map((group, groupIndex) => (
                            <React.Fragment key={`code_group_${groupIndex}`}>
                              {groupIndex > 0 && (
                                <span className={styles.codeSeparator} aria-hidden="true">
                                  -
                                </span>
                              )}
                              {/* <div className={styles.codeSlotGroup}> */}
                              {group.map(slot => (
                                <Input
                                  className={`${styles.codeSlot} ${slot.isActive ? styles.codeSlotActive : ""}`}
                                  key={`code_slot_${slot.index}`}
                                  appearance="filled-darker"
                                  value={slot.value}
                                  maxLength={1}
                                  inputMode="text"
                                  pattern="[0-9A-Fa-f]*"
                                  autoCapitalize="characters"
                                  readOnly={isEntryDisabledForRender}
                                  onFocus={() => updateLinkState({ cursorIndex: slot.index })}
                                  onClick={() => setCodeCursorIndex(slot.index)}
                                  onChange={(_event, data) => updateCodeSlot(slot.index, data.value)}
                                  onKeyDown={handleCodeSlotKeyDown(slot.index)}
                                  ref={element => {
                                    codeInputRefs.current[slot.index] = element;
                                  }}
                                />
                              ))}
                              {/* </div> */}
                            </React.Fragment>
                          ))}
                          <Button
                            icon={<BackspaceRegular />}
                            size="large"
                            className={styles.backspace}
                            onClick={removeChar}
                          />
                        </div>
                      </>
                    )}
                  </FormattedMessage>
                </div>
              </div>

              <div className={styles.keypad}>{keypadSymbolButtons}</div>
            </div>
          </DrawerBody>
        </Drawer>
        <CookieConsentModalContainer />
      </WrappedIntlProvider>
    </RuntimeFluentProvider>
  );
}

LinkRoot.propTypes = {
  store: PropTypes.object
};

export default LinkRoot;
